import { call, delay, put, select, takeLatest } from 'typed-redux-saga'
import { Action, Actions } from '~/actions'
import { CartLoadDocument, CartUpdateCountryDocument } from '~/gql'
import type { API, Country, CountryCode, State } from '~/types'
import { AUK_API_URL, getCurrentCountry, getStoreCountry, isError, isOk, shopifyFetch } from '~/utils'

export function* countrySagas() {
	yield* takeLatest('CART_COUNTRY_UPDATE', updateCountry)
	yield* takeLatest('CART_BUYER_IDENTITY_UPDATE', updateCountry)
	yield* takeLatest('CART_COUNTRY_UPDATE', updateCountryCookie)
	yield* takeLatest('CART_COUNTRY_UPDATE', updatePrefferedCountry)
	yield* takeLatest('CART_COUNTRY_UPDATE', loadDeliveryProfiles)
	yield* takeLatest('SHIPPING_SETTINGS_LOAD', loadDeliveryProfiles)
	yield* takeLatest('MARKETS_LOAD', loadMarkets)
}

function* updateCountry(action: Action<'CART_COUNTRY_UPDATE' | 'CART_BUYER_IDENTITY_UPDATE'>) {
	const { name, iso_code } = action.payload
	const id = yield* select((s: State) => s.cart.id)
	if (id && iso_code !== 'EU') {
		const cart = yield* call(() => shopifyFetch(CartLoadDocument, { id, country: iso_code }))

		const payload = { id, country: iso_code, shippingAddress: { country: name } }
		const update = yield* call(() => shopifyFetch(CartUpdateCountryDocument, payload))
		if (isOk(update)) {
			yield* put(Actions.updateCartCountrySuccesss(update.data, action.payload))
		} else if (isOk(cart)) {
			yield* put(Actions.updateCartCountrySuccesss(cart.data, action.payload))
		}
		if (isError(update)) {
			console.info('updateShippingCountryErrors', update.errors)
		}
	}
}

/** Sets Spotify country cookie */
function* updateCountryCookie(action: Action<'CART_COUNTRY_UPDATE'>) {
	const { payload: country, meta: form } = action

	const cookies = document.cookie
		.split('; ')
		.map(cookie => (cookie.includes('localization') ? `localization=${country.iso_code}` : cookie))
	document.cookie = cookies.join('; ')

	yield* call(fetch, form.action, {
		method: 'POST',
		credentials: 'include',
		redirect: 'manual',
		body: new FormData(form)
	})
}

function* updatePrefferedCountry(action: Action<'CART_COUNTRY_UPDATE'>) {
	const country: Country = action.payload
	yield localStorage.setItem('preferredCountry', JSON.stringify({ ...country, updatedDate: new Date() }))
	yield localStorage.setItem('preferredCountryCode', country.iso_code)
	yield* delay(50)
}

function* loadDeliveryProfiles(action: Action<'SHIPPING_SETTINGS_LOAD' | 'CART_COUNTRY_UPDATE'>) {
	try {
		const country = action.type === 'CART_COUNTRY_UPDATE' ? action.payload.iso_code : getCurrentCountry().iso_code
		const existing = yield* select((s: State) => s.shippingSettings)
		// Don't load profiles for countries that already have profiles
		if (existing.some(s => s.countries.includes(country as CountryCode))) return

		const req = yield* call(() => fetch(`${AUK_API_URL}/s?country=${country}&store=${getStoreCountry()}`))
		if (!req.ok) throw new Error(`Failed to load delivery profiles: ${req.statusText}`)

		const data = yield* call(() => req.json() as Promise<API.ShippingSetting | API.ShippingSetting[]>)

		yield* put(Actions.deliveryProfilesLoaded(Array.isArray(data) ? data : [data]))
	} catch (error) {
		console.error(error)
	}
}

function* loadMarkets(_action: Action<'MARKETS_LOAD'>) {
	try {
		const markets = yield* call(() => fetch(`${AUK_API_URL}/m`))
		if (!markets.ok) throw new Error(`Failed to load markets: ${markets.statusText}`)
		const data: API.MarketConfig[] = yield* call(() => markets.json())
		yield* put(Actions.marketsLoaded(data))
	} catch (error) {
		console.error(error)
	}
}
